// Share list modal — pick specific users to share a shopping list
// with. Each row toggles between "shared" and "not shared" via the
// `shopping.shareWith` / `shopping.unshareWith` RPCs.
//
// The server intentionally hides email + admin status from the
// shareable-users list (just ids), so we render usernames as-is.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type ShoppingList } from "../../lib/rpc";

interface ShareListModalProps {
  open: boolean;
  onClose: () => void;
  list: ShoppingList | null;
  notify: (msg: string) => void;
}

interface Row { id: string; shared: boolean }

export function ShareListModal({ open, onClose, list, notify }: ShareListModalProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState<Set<string>>(new Set());
  // Household-share state — initialized from the list prop and flipped
  // optimistically when the user taps the toggle row. Separate from
  // the per-user share rows because the wire shape differs (single
  // flag vs per-user records) and the UX intent is also distinct: the
  // household toggle is for "wife/kids see this", the per-user list is
  // for "share with a friend outside the household."
  const [householdShared, setHouseholdShared] = useState<boolean>(!!list?.sharedWithHousehold);
  const [householdBusy, setHouseholdBusy] = useState(false);

  useEffect(() => { setHouseholdShared(!!list?.sharedWithHousehold); }, [list]);

  // Fetch the eligible-users + current-shares pair on every open so
  // the dialog reflects the latest state. Cheap on a household-sized
  // user set.
  useEffect(() => {
    if (!open || !list) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.shoppingListShareableUsers(),
      api.shoppingListShares(list.id),
    ])
      .then(([users, shares]) => {
        if (cancelled) return;
        const sharedSet = new Set(shares.map((s) => s.userId));
        setRows(users.map((u) => ({ id: u.id, shared: sharedSet.has(u.id) })));
      })
      .catch((e: any) => notify(e?.message || "failed to load shares"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, list]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.id.toLowerCase().includes(q));
  }, [rows, filter]);

  if (!open || !list) return null;

  const sharedCount = rows.filter((r) => r.shared).length;

  const toggleHousehold = async () => {
    if (!list || householdBusy) return;
    const next = !householdShared;
    setHouseholdBusy(true);
    setHouseholdShared(next); // optimistic
    try {
      await api.shoppingUpdateList(list.id, { sharedWithHousehold: next });
      notify(next ? "Now visible to everyone in your household" : "Household sharing turned off");
    } catch (e: any) {
      setHouseholdShared(!next);
      notify(e?.message || "couldn't change household sharing");
    } finally {
      setHouseholdBusy(false);
    }
  };

  const toggle = async (row: Row) => {
    if (!list) return;
    if (busy.has(row.id)) return;
    setBusy((prev) => new Set(prev).add(row.id));
    // Optimistic flip — feels instant. Roll back on failure.
    setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, shared: !r.shared } : r));
    try {
      if (row.shared) {
        await api.shoppingUnshareWith(list.id, row.id);
        notify(`Removed ${row.id}`);
      } else {
        await api.shoppingShareWith(list.id, row.id);
        notify(`Shared with ${row.id}`);
      }
    } catch (e: any) {
      // Roll back.
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, shared: row.shared } : r));
      notify(e?.message || "share change failed");
    } finally {
      setBusy((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    }
  };

  return (
    <div className="sl-bg" onClick={onClose}>
      <div className="sl-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="sl-head">
          <span className="sl-head-icon"><Icon name="people" size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sl-head-title">Share "{list.name}"</div>
            <div className="sl-head-sub">
              {sharedCount === 0 ? "Not shared with anyone yet."
                : sharedCount === 1 ? "Shared with 1 person."
                : `Shared with ${sharedCount} people.`}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={13} />
          </button>
        </header>
        <div className="sl-body">
          {/* Household-share row sits above the per-user list — it's
              the common case (share with spouse/family) and clearly
              labeled so users don't confuse it with the per-person
              section below. */}
          <button
            className="sl-row sl-row-household"
            onClick={toggleHousehold}
            type="button"
            disabled={householdBusy}
          >
            <span className="sl-avatar sl-avatar-household"><Icon name="home" size={14} /></span>
            <span className="sl-row-name">
              <span className="sl-row-title">Everyone in my household</span>
              <span className="sl-row-sub">{householdShared ? "Visible to all members" : "Personal — only you"}</span>
            </span>
            <span className="sl-toggle" data-on={householdShared}>
              <span className="sl-toggle-dot" />
            </span>
          </button>
          {rows.length > 6 && (
            <input
              type="text"
              className="sl-search"
              placeholder="Filter…"
              value={filter}
              onInput={(e) => setFilter((e.currentTarget as HTMLInputElement).value)}
            />
          )}
          {loading
            ? <p className="sl-empty">loading…</p>
            : rows.length === 0
              ? <p className="sl-empty">No other users to share with.</p>
              : filtered.length === 0
                ? <p className="sl-empty">No matches.</p>
                : (
                  <div className="sl-list">
                    {filtered.map((r) => (
                      <button
                        key={r.id}
                        className="sl-row"
                        onClick={() => toggle(r)}
                        type="button"
                        disabled={busy.has(r.id)}
                      >
                        <span className="sl-avatar">{r.id.slice(0, 1).toUpperCase()}</span>
                        <span className="sl-row-name">{r.id}</span>
                        <span className="sl-toggle" data-on={r.shared}>
                          <span className="sl-toggle-dot" />
                        </span>
                      </button>
                    ))}
                  </div>
                )
          }
        </div>
        <footer className="sl-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose} type="button">Done</button>
        </footer>
      </div>
    </div>
  );
}
